import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useToken } from "../utils";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../App";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import IconButton from "@mui/material/IconButton";
import VerifyEmailDialog from "./VerifyEmailDialog";
import ClerkSocialButtons from "./ClerkSocialButtons";
import { isClerkEnabled } from "../lib/clerk";
import {
  validateSignupEmail,
  validateSignupName,
} from "../lib/authValidation";

import { AUTH_API_URL, authFetch } from "../lib/authFetch";

console.log("AUTH_API_URL:", AUTH_API_URL);

async function loginUser(credentials: { email: string; password: string }) {
  const res = await authFetch(`${AUTH_API_URL}/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(credentials),
  }).then((data) => data.json());

  return res;
}

async function createAndLoginUser(credentials: {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
}) {
  const url = `${AUTH_API_URL}/register`;
  console.log("Fetching:", url);
  console.log("Request body:", credentials);
  
  try {
    const res = await authFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });
    
    console.log("Response status:", res.status, res.statusText);
    
    let data;
    try {
      data = await res.json();
      console.log("Response data:", data);
    } catch (jsonError) {
      console.error("Failed to parse JSON response:", jsonError);
      const text = await res.text();
      console.error("Response text:", text);
      throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
    }
    
    if (!res.ok) {
      // If response is not OK, throw an error with the detail/message
      throw new Error(data.detail || data.message || `Registration failed: ${res.status}`);
    }
    
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new Error(`Network error: Cannot reach ${url}. Is the auth-api server running?`);
    }
    throw error;
  }
}

const requiredAsterisk = <span style={{ color: "red" }}> *</span>;

const passwordInputWrapperStyle: React.CSSProperties = {
  position: "relative",
};

const passwordToggleStyle: React.CSSProperties = {
  position: "absolute",
  right: 4,
  top: "50%",
  transform: "translateY(-50%)",
};

type AuthVariant = "standalone" | "embedded";
type AuthMode = "signin" | "signup";
type TokenForVerifyCallback = (token: string) => void;

type Props = {
  setToken: (token: string) => void;
  variant?: AuthVariant;
  initialMode?: AuthMode;
  /** Fired when the user toggles Sign In ↔ Sign Up (used by AuthModal chrome). */
  onModeChange?: (mode: AuthMode) => void;
};

export default function Auth({
  setToken,
  variant = "standalone",
  initialMode,
  onModeChange,
}: Props) {
  let [authMode, setAuthMode] = useState<string>(initialMode ?? "signin");

  useEffect(() => {
    if (initialMode) setAuthMode(initialMode);
  }, [initialMode]);

  // sign in props
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // signup props — email-first step, then name + password
  const [signupStep, setSignupStep] = useState<1 | 2>(1);
  const [signupPassword, setSignupPassword] = useState("");
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  // Email verification state. The dialog owns resend/cooldown logic internally;
  // we only track the token and email to display.
  const [pendingToken, setPendingToken] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [initialDeliveryFailed, setInitialDeliveryFailed] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const { setToken: saveToken } = useToken();
  const navigate = useNavigate();
  const { darkMode, setDarkMode } = useDarkMode();

  const isEmbedded = variant === "embedded";
  const containerClassName = [
    "Auth-form-container",
    darkMode ? "dark" : "",
    isEmbedded ? "embedded" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const logoSrc = darkMode ? "/images/logo_dark.png" : "/images/logo_light.jpg";

  // When embedded in a modal, the surrounding chrome (PublicHeader dark-mode
  // toggle, modal framing) already handles these concerns, so we suppress them
  // to keep the form compact.
  const darkModeToggle = isEmbedded ? null : (
    <IconButton
      onClick={() => setDarkMode((prev) => !prev)}
      size="small"
      className="Auth-dark-toggle"
      aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
    >
      {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
    </IconButton>
  );
  const logoEl = isEmbedded ? null : (
    <img src={logoSrc} alt="Logo" className="Auth-form-img" />
  );

  const completeLogin: TokenForVerifyCallback = (jwt) => {
    saveToken(jwt);
    setToken(jwt);
    navigate("/");
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const res = await loginUser({ email, password });
    if (!res.token) {
      alert(res.message || "Login failed");
      return;
    }

    // Check verification status via the /me-style endpoint on the auth-api
    // using the freshly issued token. If the account is unverified, open the
    // dialog instead of navigating so returning users don't get stuck behind
    // the soft-block banner.
    try {
      const meRes = await authFetch(`${AUTH_API_URL}/me`, {
        headers: { "Authorization": `Bearer ${res.token}` },
      });
      if (meRes.ok) {
        const me = await meRes.json();
        if (me && me.email_verified === false) {
          setPendingToken(res.token);
          setPendingEmail(me.email || email);
          setVerifyOpen(true);
          return;
        }
      }
    } catch {
      // If the /me lookup fails for any reason, fall through to a normal login.
      // The banner will still appear once the app loads the user profile.
    }

    completeLogin(res.token);
  };

  const handleSignupContinue = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const trimmed = signupEmail.trim();
    const emailError = validateSignupEmail(trimmed);
    if (emailError) {
      alert(emailError);
      return;
    }
    setSignupEmail(trimmed);
    setSignupStep(2);
  };

  const handleSignup = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const firstname = signupFirstName.trim();
    const lastname = signupLastName.trim();
    const nameError = validateSignupName(firstname, lastname);
    if (nameError) {
      alert(nameError);
      return;
    }
    if (!signupPassword || signupPassword.length < 6) {
      alert("Please choose a password with at least 6 characters");
      return;
    }
    try {
      const res = await createAndLoginUser({
        email: signupEmail.trim(),
        password: signupPassword,
        firstname,
        lastname,
      });
      if (res.token && res.needs_verification) {
        setPendingToken(res.token);
        setPendingEmail(signupEmail.trim());
        setInitialDeliveryFailed(res.email_sent === false);
        setVerifyOpen(true);
      } else if (res.token) {
        completeLogin(res.token);
      } else {
        alert(res.detail || res.message || "Registration failed");
      }
    } catch (error) {
      console.error("Signup error:", error);
      const errorMessage = error instanceof Error ? error.message : "ERROR OCCURRED WHILE TRYING TO CREATE USER!";
      alert(errorMessage);
    }
  };

  const changeAuthMode = () => {
    const next: AuthMode = authMode === "signin" ? "signup" : "signin";
    setAuthMode(next);
    setSignupStep(1);
    onModeChange?.(next);
  };

  const verifyDialog = (
    <VerifyEmailDialog
      open={verifyOpen}
      token={pendingToken}
      email={pendingEmail}
      initialDeliveryFailed={initialDeliveryFailed}
      darkMode={darkMode}
      onVerified={() => {
        setVerifyOpen(false);
        setInitialDeliveryFailed(false);
        completeLogin(pendingToken);
      }}
      onClose={() => {
        setVerifyOpen(false);
        setInitialDeliveryFailed(false);
      }}
    />
  );

  if (authMode === "signin") {
    return (
      <>
      <div className={containerClassName}>
        {darkModeToggle}
        {logoEl}
        <form className="Auth-form" onSubmit={handleSubmit} data-testid="auth-signin-form" noValidate>
          <div className="Auth-form-content">
            <h3 className="Auth-form-title">Sign In</h3>
            <div className="text-center Auth-form-switch">
              Not registered yet?{" "}
              <button type="button" className="link-primary Auth-mode-toggle" onClick={changeAuthMode}>
                Sign Up
              </button>
            </div>
            {isClerkEnabled() ? <ClerkSocialButtons mode="signin" /> : null}
            {isClerkEnabled() ? (
              <div className="Auth-social-divider" aria-hidden="true">
                <span>or</span>
              </div>
            ) : null}
            <div className="form-group mt-3">
              <label>Email address{requiredAsterisk}</label>
              <input
                type="email"
                data-testid="auth-email"
                onChange={(e) => setEmail(e.target.value)}
                className="form-control mt-1"
                placeholder="Email Address"
                required
              />
            </div>
            <div className="form-group mt-3">
              <label>Password{requiredAsterisk}</label>
              <div style={passwordInputWrapperStyle} className="mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  data-testid="auth-password"
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-control"
                  placeholder="Enter password"
                  required
                  style={{ paddingRight: 40 }}
                />
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  size="small"
                  style={passwordToggleStyle}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </div>
            </div>
            <div className="d-grid gap-2 mt-3">
              <button type="submit" className="btn btn-primary Auth-submit" data-testid="auth-submit">
                Sign in
              </button>
            </div>
            <p className="text-center mt-2 Auth-forgot">
              Forgot{" "}
              <a
                href="/forgot-password"
                onClick={(event) => {
                  event.preventDefault();
                  navigate("/forgot-password");
                }}
              >
                password?
              </a>
            </p>
          </div>
        </form>
      </div>
      {verifyDialog}
      </>
    );
  } else if (signupStep === 1) {
    return (
      <>
      <div className={containerClassName}>
        {darkModeToggle}
        {logoEl}
        <form
          className="Auth-form"
          onSubmit={handleSignupContinue}
          data-testid="auth-signup-form"
          noValidate
        >
          <div className="Auth-form-content">
            <h3 className="Auth-form-title">Sign Up</h3>
            <div className="text-center Auth-form-switch">
              Already registered?{" "}
              <button type="button" className="link-primary Auth-mode-toggle" onClick={changeAuthMode}>
                Sign In
              </button>
            </div>
            {isClerkEnabled() ? <ClerkSocialButtons mode="signup" /> : null}
            {isClerkEnabled() ? (
              <div className="Auth-social-divider" aria-hidden="true">
                <span>or</span>
              </div>
            ) : null}
            <div className="form-group mt-3">
              <label>Email address{requiredAsterisk}</label>
              <input
                onChange={(e) => setSignupEmail(e.target.value)}
                type="email"
                data-testid="auth-email"
                className="form-control mt-1"
                placeholder="Email Address"
                autoComplete="email"
                required
              />
            </div>
            <div className="d-grid gap-2 mt-3">
              <button
                type="submit"
                className="btn btn-primary Auth-submit"
                data-testid="auth-signup-continue"
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
      {verifyDialog}
      </>
    );
  } else {
    return (
      <>
      <div className={containerClassName}>
        {darkModeToggle}
        {logoEl}
        <form className="Auth-form" onSubmit={handleSignup} data-testid="auth-signup-form" noValidate>
          <div className="Auth-form-content">
            <button
              type="button"
              className="Auth-back link-primary"
              data-testid="auth-signup-back"
              onClick={() => {
                setSignupStep(1);
                setSignupPassword("");
              }}
            >
              ← Back
            </button>
            <h3 className="Auth-form-title">Create account</h3>
            <p className="Auth-step-email text-center" title={signupEmail}>
              {signupEmail}
            </p>
            <div className="form-group mt-3">
              <label>First name{requiredAsterisk}</label>
              <input
                onChange={(e) => setSignupFirstName(e.target.value)}
                type="text"
                data-testid="auth-signup-firstname"
                className="form-control mt-1"
                placeholder="Jane"
                autoComplete="given-name"
                required
              />
            </div>
            <div className="form-group mt-3">
              <label>Last name{requiredAsterisk}</label>
              <input
                onChange={(e) => setSignupLastName(e.target.value)}
                type="text"
                data-testid="auth-signup-lastname"
                className="form-control mt-1"
                placeholder="Doe"
                autoComplete="family-name"
                required
              />
            </div>
            <div className="form-group mt-3">
              <label>Password{requiredAsterisk}</label>
              <div style={passwordInputWrapperStyle} className="mt-1">
                <input
                  onChange={(e) => setSignupPassword(e.target.value)}
                  type={showSignupPassword ? "text" : "password"}
                  data-testid="auth-password"
                  className="form-control"
                  placeholder="Password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  style={{ paddingRight: 40 }}
                />
                <IconButton
                  onClick={() => setShowSignupPassword(!showSignupPassword)}
                  size="small"
                  style={passwordToggleStyle}
                  tabIndex={-1}
                  aria-label={showSignupPassword ? "Hide password" : "Show password"}
                >
                  {showSignupPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </div>
            </div>
            <div className="d-grid gap-2 mt-3">
              <button
                type="submit"
                className="btn btn-primary Auth-submit"
                data-testid="auth-submit"
              >
                Create account
              </button>
            </div>
            <div className="text-center Auth-form-switch mt-3">
              Already registered?{" "}
              <button type="button" className="link-primary Auth-mode-toggle" onClick={changeAuthMode}>
                Sign In
              </button>
            </div>
          </div>
        </form>
      </div>
      {verifyDialog}
      </>
    );
  }
}

Auth.propTypes = {
  setToken: PropTypes.func.isRequired,
};
